import React, { Fragment, useEffect, useState } from 'react';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';

import {
  requestInsert,
  requestUpdate,
  requestDelete,
  requestInform as request,
  requestFileDelete,
  requestFileDownload
} from 'api/board/file';
import cloudUpload from 'assets/img/icons/cloud-upload.svg';
import CardDropdown from 'components/common/CardDropdown';
import Flex from 'components/common/Flex';
import Editor from 'components/common-sfims/form/Editor';
import Modal from 'components/common-sfims/modal/Common';
import { onEnterKeyPress, onInputChange } from 'function/event';
import { downloadFile } from 'function/file';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import { getSize } from 'helpers/utils';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import { RETURN_YN } from 'types/type';
import format from 'utils/string/format';

/**
 * 협업 게시판 > 자료실 - 등록/수정/조회
 */
const Inform = ({ show, seq, setting, setSetting, onHide, onSetting }) => {
  /* variable */
  const { seq: loginSeq, adminYn } = getLoginInform();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* dropzone */
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 10,
    maxSize: 50000000,
    onDrop: (files, [{ errors: [error = {}] = [] } = {}]) => {
      try {
        switch (error.code) {
          case 'too-many-files':
            throw format(getText('tooManyFilesMessage'), { max: 10 });

          case 'file-too-large':
            throw 'fileTooLarge';
        }

        if (
          files.length +
            fileData.files.length +
            inform.fileAttachments?.length >
          10
        ) {
          throw format(getText('tooManyFilesMessage'), { max: 10 });
        }

        if (
          files.some(({ name }) =>
            ['exe', 'bat', 'msi'].includes(name.substr(-3).toLowerCase())
          )
        ) {
          throw 'notAcceptFileExe';
        }

        setFileData({
          files: [...fileData.files, ...files]
        });
      } catch (message) {
        setConfirmModal({
          isOpen: true,
          message
        });
      }
    }
  });

  /* state */
  // 자료실 등록/수정 요청 데이터
  const [fileData, setFileData] = useObjReducer({ files: [] });
  // 자료실 정보
  const [inform, setInform] = useState({});
  // 삭제 비활성화 여부
  const [isDisableDelete, setIsDisableDelete] = useState(false);
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // 에디터 표시 여부
  const [isShowEditor, setIsShowEditor] = useState(false);

  /* watch */
  useEffect(() => {
    // 모달 표시 여부에 따라 에디터 표시/숨김
    setIsShowEditor(show);

    // 모달이 표시된 경우
    if (!show) {
      return;
    }

    // 데이터 초기화
    setFileData({
      title: '',
      files: [],
      content: ''
    });
    setInform({});

    // 수정/조회 화면인 경우 자료실 정보 조회 요청
    if (seq) {
      requestInform();
    }
  }, [show]);

  /* method */
  // 첨부 파일 다운로드 요청
  const requestDownload = async seq => {
    try {
      const { data, name } = await requestFileDownload(seq);

      downloadFile(data, name);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    }
  };
  // 자료실 정보 조회 요청
  const requestInform = async () => {
    try {
      const { data = {}, code, message } = await request(seq);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setFileData({
        title: data.title ?? '',
        content: data.content ?? ''
      });
      setInform(data);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });

      // 모달 숨김
      onHide();
    }
  };

  /* event */
  // 삭제 클릭 시
  const onDeleteClick = () => {
    setConfirmModal({
      isOpen: true,
      title: 'delete',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      async onConfirm() {
        setIsDisableDelete(true);

        try {
          const { code, message } = await requestDelete(seq);

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 설정 시 이벤트 실행
          onSetting();

          // 모달 숨김
          onHide();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
          setIsDisableDelete(false);
        }
      }
    });
  };
  // 첨부 파일 삭제 클릭 시
  const onDeleteFileClick = (index, isReal = false) => {
    if (isReal) {
      setConfirmModal({
        isOpen: true,
        title: 'delete',
        message: 'deleteMessage',
        isCancelBtn: true,
        btnNm: 'delete',
        async onConfirm() {
          try {
            const { code, message } = await requestFileDelete({
              fileAttachments: [{ seq: index }]
            });

            if (code !== RESPONSE_CODE.Success) {
              throw {
                code,
                error: message
              };
            }

            // 설정 시 이벤트 실행
            onSetting();

            // 자료실 정보 조회 요청
            await requestInform();
          } catch (error) {
            setConfirmModal({
              isOpen: true,
              message: getErrorMessage(error?.code, error?.error)
            });
          }
        }
      });

      return;
    }

    setFileData({
      files: fileData.files.filter((value, idx) => idx !== index)
    });
  };
  // 설정
  const onSubmit = async () => {
    if (isDisableSetting) {
      return;
    }

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(fileData, seq ? ['files'] : [])) {
        throw {
          code: RESPONSE_CODE[seq ? 'BadRequestUpdate' : 'BadRequestInsert']
        };
      }

      if (seq) {
        const { code, message } = await requestUpdate(seq, fileData);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      } else {
        const { code, message } = await requestInsert(fileData);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      }

      // 설정 시 이벤트 실행
      onSetting();

      // 모달 숨김
      onHide();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableSetting(false);
    }
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{
        body: 'bg-light max-vh-75 scrollbar',
        footer: 'align-items-center justify-content-center'
      }}
      size="xl"
      onHide={onHide}
    >
      <Fragment key="header">
        {getText(setting ? (seq ? 'updateFile' : 'insertFile') : 'boardFile')}
      </Fragment>
      <Fragment key="body">
        <Form.Group className="mb-3">
          <Form.Label>
            {setting && '* '}
            {getText('title')}
          </Form.Label>
          {/* view일 경우 readOnly 삽입 */}
          <Form.Control
            readOnly={!setting}
            size="sm"
            type="text"
            value={fileData.title}
            autoFocus={setting}
            name="title"
            placeholder={getText('boardTitlePlaceholder')}
            onChange={event => {
              if (setting) {
                onInputChange(event, setFileData);
              }
            }}
            onKeyUp={event => onEnterKeyPress(event, onSubmit)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>
            {setting && '* '}
            {getText('file')}
          </Form.Label>
          {setting && (
            <div {...getRootProps({ className: 'dropzone-area py-3' })}>
              <input {...getInputProps()} />
              <Flex
                justifyContent="center"
                alignItems="center"
                className="h-100"
              >
                <img
                  src={cloudUpload}
                  alt="Drop your files here"
                  className="me-2"
                  width={25}
                />
                <p className="fs-0 mb-0 text-700">Drop your files here</p>
              </Flex>
            </div>
          )}
          <Flex wrap="wrap" className="row-cols-5">
            {inform.fileAttachments?.map?.(({ seq, originNm } = {}) => (
              <div key={`fileAttachments${seq}`}>
                <Flex
                  alignItems="center"
                  className="py-3 px-2 border-bottom btn-reveal-trigger"
                >
                  <Flex
                    justifyContent="between"
                    alignItems="center"
                    className="flex-1"
                  >
                    <div>
                      <h6>{originNm}</h6>
                    </div>
                  </Flex>
                  <CardDropdown>
                    <div className="py-2">
                      {setting ? (
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() => onDeleteFileClick(seq, true)}
                        >
                          {getText('delete')}
                        </Dropdown.Item>
                      ) : (
                        <Dropdown.Item onClick={() => requestDownload(seq)}>
                          {getText('download')}
                        </Dropdown.Item>
                      )}
                    </div>
                  </CardDropdown>
                </Flex>
              </div>
            ))}
            {fileData.files.map(({ name, size }, index) => (
              <div key={`files${index}`}>
                <Flex
                  alignItems="center"
                  className="py-3 px-2 border-bottom btn-reveal-trigger"
                >
                  <Flex
                    justifyContent="between"
                    alignItems="center"
                    className="flex-1"
                  >
                    <div>
                      <h6>{name}</h6>
                      <Flex alignItems="center" className="position-relative">
                        <p className="mb-0 fs--1 text-400 line-height-1">
                          <strong>{getSize(size)}</strong>
                        </p>
                      </Flex>
                    </div>
                  </Flex>
                  <CardDropdown>
                    <div className="py-2">
                      <Dropdown.Item
                        className="text-danger"
                        onClick={() => onDeleteFileClick(index)}
                      >
                        {getText('delete')}
                      </Dropdown.Item>
                    </div>
                  </CardDropdown>
                </Flex>
              </div>
            ))}
          </Flex>
        </Form.Group>
        <Form.Group>
          <Form.Label>
            {setting && '* '}
            {getText('content')}
          </Form.Label>
          {setting ? (
            isShowEditor && (
              <Editor
                value={fileData.content}
                onUpdate={(content = '') => setFileData({ content })}
              />
            )
          ) : (
            <>
              {/* view일 경우 */}
              <div
                className="p-3 bg-white"
                dangerouslySetInnerHTML={{ __html: inform.content }}
              />
            </>
          )}
        </Form.Group>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText(setting ? 'cancel' : 'close')}
        </Button>
        {setting ? (
          <Button
            disabled={isDisableSetting}
            className="ms-2"
            onClick={onSubmit}
          >
            {getText('ok')}
          </Button>
        ) : (
          (adminYn === RETURN_YN.Y || loginSeq === inform.regUsr) && (
            <>
              <Button
                variant="success"
                className="ms-2"
                onClick={() => setSetting(true)}
              >
                {getText('update')}
              </Button>
              <Button
                disabled={isDisableDelete}
                variant="danger"
                className="ms-2"
                onClick={onDeleteClick}
              >
                {getText('delete')}
              </Button>
            </>
          )
        )}
      </Fragment>
    </Modal>
  );
};

Inform.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 자료실 번호
  seq: PropTypes.number,

  // 설정 여부
  setting: PropTypes.bool,

  // 설정 여부 설정 함수
  setSetting: PropTypes.func,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 설정 시 이벤트
  onSetting: PropTypes.func
};
Inform.defaultProps = {
  show: false,
  setting: false
};

export default Inform;
